import { useEffect, useState } from "react";
import { CheckIcon, Mic, Pause, Play, Square, Volume2, X } from "lucide-react";

import { AudioProcessingLoader } from "components/AudioProcessingLoader/AudioProcessingLoader";
import { RichEditor } from "components/RichEditor/RichEditor";
import { Button } from "components/ui/Button";
import { useIcdSearch } from "features/consultations/hooks/useIcdSearch.js";
import { useNoteAudio } from "features/consultations/hooks/useNoteAudio.js";
import { useNoteEditor } from "features/consultations/hooks/useNoteEditor.js";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientMedicalHistoryByConsultation } from "features/patients/hooks/usePatientMedicalHistory";
import { useMainService } from "features/practice/hooks/useMainService";

import { ChooseTemplateModal } from "./components/ChooseTemplateModal/ChooseTemplateModal.jsx";
import { IcdDropdown } from "./components/IcdDropdown/IcdDropdown.jsx";
import style from "./ConsultationEdit.module.css";

const isHtml = (str) => typeof str === "string" && /<[a-z][\s\S]*>/i.test(str);

const plainToHtml = (str) =>
  (str ?? "").replace(/\n/g, "<br>");

// Append a template's text to the existing note while preserving its line
// breaks. When either side is HTML the note renders in Quill, which collapses
// raw "\n" — so we convert plain segments to HTML and join with <br>. When both
// are plain text the RichEditor uses a pre-wrap textarea, so "\n" is kept.
const combineNotes = (existing, addition) => {
  if (!existing) return addition;

  if (isHtml(existing) || isHtml(addition)) {
    const left = isHtml(existing) ? existing : plainToHtml(existing);
    const right = isHtml(addition) ? addition : plainToHtml(addition);

    return `${left}<br>${right}`;
  }

  return `${existing}\n${addition}`;
};

const ConsultationEdit = ({
  isDisabled,
  isReadOnly = false,
  autoSave = true,
  canCreateNote,
  consultation,
  staffName,
  selectedNote,
  onNoteChange,
  onNewNoteSaved,
}) => {
  const effectiveCanCreateNote = canCreateNote ?? !isReadOnly;
  const patientId = useCurrentPatientId();
  const tenantVal = localStorage.getItem("kinesin-tenant");
  const [editorKey, setEditorKey] = useState(0);
  const bumpEditorKey = () => setEditorKey((prev) => prev + 1);

  const { data: mainService } = useMainService(
    consultation?.mainPracticeServiceId
  );
  const {
    data: consultationMedicalHistory,
    isLoading: isLoadingMedicalHistory,
  } = usePatientMedicalHistoryByConsultation(consultation?.id);

  const audio = useNoteAudio({
    consultationId: consultation?.id,
    tenantVal,
    onTranscribed: (data) => {
      noteEditor.setNote(data.note);
      noteEditor.updateNotes(data.note);
      bumpEditorKey();
    },
  });

  const noteEditor = useNoteEditor({
    consultationId: consultation?.id,
    patientId,
    onAudioLoad: audio.loadAudioForPlayback,
    onAudioClear: () => audio.setAudioUrl(null),
    onNewNoteSaved,
  });

  const icd = useIcdSearch({ consultationId: consultation?.id, patientId });

  useEffect(() => {
    if (selectedNote) {
      noteEditor.handleEditNote(selectedNote, () =>
        onNoteChange?.(selectedNote.id)
      );
      bumpEditorKey();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNote]);

  return (
    <>
      <form className={`${style.editConsultForm} ${isReadOnly ? style.editConsultFormLocked : ""}`}>
        <div className={style.inputGroupGrid4}>
          <div>
            <label className={style.formLabel}>Code consultation</label>
            <div ref={icd.icdSearchRef} style={{ position: "relative" }}>
              <input
                type="text"
                className={`form-control ${(isDisabled || isReadOnly || !consultation?.id) ? style.readOnlyInput : ""}`}
                placeholder="Search for ICD-10 codes..."
                value={icd.icdSearch}
                onChange={(e) => {
                  icd.setIcdSearch(e.target.value);
                  icd.setIsIcdDropdownOpen(true);
                }}
                onFocus={() => icd.setIsIcdDropdownOpen(true)}
                disabled={isDisabled || isReadOnly || !consultation?.id}
                readOnly={isReadOnly}
              />
              {icd.isIcdDropdownOpen && icd.icdSearch && (
                <IcdDropdown
                  search={icd.icdSearch}
                  codes={icd.icd10Codes}
                  isLoading={icd.isLoadingIcd}
                  onSelect={icd.handleIcdSelect}
                />
              )}
            </div>
          </div>
          <div>
            <label className={style.formLabel}>Consultation type</label>
            <input
              name="consultationType"
              readOnly
              type="text"
              className={`form-control ${style.readOnlyInput}`}
              value={mainService?.name ?? ""}
            />
          </div>
          <div>
            <label className={style.formLabel}>Consultation date</label>
            <input
              name="consultationDate"
              readOnly
              type="text"
              className={`form-control ${style.readOnlyInput}`}
              value={consultation?.created}
            />
          </div>
          <div>
            <label className={style.formLabel}>Consultation Professional</label>
            <input
              name="doctorName"
              readOnly
              type="text"
              className={`form-control ${style.readOnlyInput}`}
              value={staffName ?? ""}
            />
          </div>
        </div>

        {consultationMedicalHistory?.length > 0 && (
          <div className={style.icdPillsRow}>
            {isLoadingMedicalHistory ? (
              <span style={{ fontSize: "14px", color: "#6b7280" }}>
                Loading codes...
              </span>
            ) : (
              consultationMedicalHistory.map((item) => (
                <div key={item.id} className={style.icdPill}>
                  <span className={style.icdPillCode}>{item.diseaseCode}</span>
                  <span className={style.icdPillDetails}>
                    {item.medicalDetails}
                  </span>
                  {!isDisabled && !isReadOnly && (
                    <button
                      type="button"
                      className={style.icdPillRemove}
                      onClick={() => icd.handleIcdRemove(item)}
                      disabled={icd.isDeletingMedicalHistory}
                      title="Delete ICD-10 code"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        <div>
          {!isReadOnly && (
            <div className={style.noteHeader}>
              <div className={style.noteHeaderLeft}>
                <ChooseTemplateModal
                  onSelect={(newNote) => {
                    const combined = combineNotes(noteEditor.note, newNote);
                    noteEditor.setNote(combined);
                    noteEditor.updateNotes(combined);
                    bumpEditorKey();
                  }}
                  isDisabled={isDisabled}
                />
                <div className={style.audioControls}>
                  <Button
                    type="button"
                    variant={audio.isRecording ? "danger" : "outline"}
                    size="medium"
                    onClick={
                      audio.isRecording
                        ? audio.stopRecording
                        : audio.startRecording
                    }
                    disabled={isDisabled || audio.isProcessing}
                  >
                    {audio.isRecording ? (
                      <>
                        <Square size={14} /> Stop Recording
                      </>
                    ) : audio.isProcessing ? (
                      <AudioProcessingLoader text="Processing..." />
                    ) : (
                      <>
                        <Mic size={14} /> Record Audio
                      </>
                    )}
                  </Button>
                  <span className={style.betaText}>
                    (Audio recording is in beta testing)
                  </span>
                </div>
              </div>
            </div>
          )}

          {audio.isProcessing && (
            <div className={style.processingAlertInfo}>
              <AudioProcessingLoader text="" />
              You cannot update the note while the audio is processing, bear
              with us as processing Irish accents can take time
            </div>
          )}

          <RichEditor
            key={`${editorKey}-${isDisabled}-${isReadOnly}`}
            editorValue={noteEditor.note}
            disabled={isDisabled}
            loading={audio.isProcessing}
            readOnly={isReadOnly}
            allowImages={false}
            onChange={(value) => {
              noteEditor.setNote(value);
              if (autoSave) noteEditor.updateNotes(value);
            }}
          />

          {audio.isProcessing && (
            <div className={style.processingAlertWarning}>
              We advise you review the output from the health scribe, as no one
              is perfect
            </div>
          )}

          {audio.audioUrl &&
            noteEditor.consultationNotes?.at(0)?.audioFilePath && (
              <div className={style.audioPlaybackContainer}>
                <div className={style.audioPlaybackHeader}>
                  <Volume2 size={16} className={style.audioVolumeIcon} />
                  <span className={style.audioPlaybackTitle}>
                    Recorded Audio
                  </span>
                </div>
                <div className={style.audioPlaybackControls}>
                  <Button
                    type="button"
                    variant="outline"
                    size="small"
                    onClick={audio.toggleAudioPlayback}
                  >
                    {audio.isPlaying ? (
                      <>
                        <Pause size={14} /> Pause
                      </>
                    ) : (
                      <>
                        <Play size={14} /> Play
                      </>
                    )}
                  </Button>
                  <audio
                    ref={audio.audioRef}
                    src={audio.audioUrl}
                    onEnded={() => audio.setIsPlaying(false)}
                    onPlay={() => audio.setIsPlaying(true)}
                    onPause={() => audio.setIsPlaying(false)}
                    style={{ display: "none" }}
                  />
                </div>
              </div>
            )}
        </div>
      </form>

      {(!isReadOnly || effectiveCanCreateNote) && (
        <div className={`${style.bottomSectionWrapper} ${style.bottomSectionWrapperRight}`}>
          <div className={style.bottomSectionRight}>
            {!isReadOnly && autoSave && (
              <p className={isDisabled ? style.autoSaveDisabled : ""}>
                <CheckIcon width={16} height={16} />
                Auto saved
              </p>
            )}
            <Button
              type="button"
              variant="primary"
              size="large"
              disabled={isDisabled}
              onClick={() => {
                noteEditor.handleNewNote(() => onNoteChange?.(null), isReadOnly);
                bumpEditorKey();
              }}
            >
              New Note
            </Button>
            {!isReadOnly && !autoSave && (
              <Button
                type="button"
                variant="primary"
                size="large"
                disabled={isDisabled || !noteEditor.note}
                onClick={() => {
                  noteEditor.saveNow(noteEditor.note);
                  noteEditor.setShowSave(true);
                }}
              >
                Save Note
              </Button>
            )}
          </div>
        </div>
      )}

    </>
  );
};

export { ConsultationEdit };
